import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import ReactQuill from "react-quill";
import axiosInstance from "../../../utilities/axios";
import "react-quill/dist/quill.snow.css";
import "./quill.css";

const Comments = ({ projectId, issueId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [editStates, setEditStates] = useState({});
  const [editComment, setEditComment] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const org = JSON.parse(localStorage.getItem("org"));
  const user = JSON.parse(localStorage.getItem("user"));

  const getInitials = (name = "") => {
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase();
  };

  // Fetch comments for this issue
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const { data } = await axiosInstance.get(
          `/api/v2/issues/${projectId}/issue/${issueId}/comments`
        );
        setComments(data.comments);
      } catch (err) {
         // console.error("Error fetching comments", err);
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, [projectId, issueId]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          ["link", "image"],
          ["clean"],
        ],
      },
    }),
    []
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];
  const moveFileToFolder = async (resourceId, folderId, ownerId) => {
    try {
      await axiosInstance.post(
        "https://api.files.clikkle.com:4500/api/fs/move",
        {
          newParentId: folderId,
          resourceId,
          userId: ownerId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    } catch (error) {
       // console.error("Error moving comment file:", error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await axiosInstance.post(
        `/api/v2/issues/${projectId}/issue/${issueId}/comment`,
        {
          userId: user._id,
          text: newComment,
          name: user.firstName + " " + user.lastName,
          ownerId: org.ownerId,
          ownerEmail: user.recoveryEmail,
        }
      );
      if (res.data.uploadedResources?.length) {
        res.data.uploadedResources.forEach((resourceId) => {
          moveFileToFolder(resourceId, org.folderId, org.ownerId);
        });
      }
      setComments((prev) => [...prev, res.data.comment]);
      setNewComment("");
    } catch (err) {
       // console.error("Error adding comment", err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axiosInstance.delete(
        `/api/v2/issues/${projectId}/issue/${issueId}/comment/${commentId}`
      );
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
       // console.error("Error deleting comment", err);
    }
  };

  const handleEditInit = (id, text) => {
    setEditStates({ ...editStates, [id]: true });
    setEditComment(text);
  };

  const handleCancelEdit = (id) => {
    setEditStates({ ...editStates, [id]: false });
    setEditComment("");
  };

  const handleSaveEdit = async (id) => {
    setEditLoading(true);
    try {
      const res = await axiosInstance.patch(
        `/api/v2/issues/${projectId}/issue/${issueId}/comment/${id}`,
        { text: editComment }
      );
      setComments((prev) =>
        prev.map((c) => (c._id === id ? res.data.comment : c))
      );
      handleCancelEdit(id);
    } catch (err) {
       // console.error("Error updating comment", err);
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <>
      <Typography variant="h6" sx={{ my: 1 }}>
        Add new comment
      </Typography>

      <Box mb={2}>
        <ReactQuill
          theme="snow"
          value={newComment}
          onChange={setNewComment}
          modules={modules}
          formats={formats}
        />
        <Box mt={1}>
          <Button variant="contained" size="small" onClick={handleAddComment}>
            Add
          </Button>
        </Box>
      </Box>

      {/* Comments list */}
      <Box>
        {comments.map((comment) => {
          const timeAgo = formatTimeAgo(
            Date.now() - new Date(comment.createdAt)
          );
          return (
            <Box key={comment._id} display="flex" alignItems="start" mb={2}>
              <Avatar
                sx={{ height: 30, width: 30, mr: 2 }}
                style={{ fontSize: "small" }}
              >
                {getInitials(comment.name)}
              </Avatar>

              <Box>
                <Typography
                  variant="body2"
                  color="text.tertiary"
                  sx={{ mb: 0.5 }}
                >
                  {comment.name}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 3 }}
                  >
                    {timeAgo}
                  </Typography>
                </Typography>

                {editStates[comment._id] ? (
                  <>
                    <ReactQuill
                      theme="snow"
                      value={editComment}
                      onChange={setEditComment}
                      modules={modules}
                      formats={formats}
                    />
                    <Box mt={1}>
                      <Button
                        disabled={editLoading}
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveEdit(comment._id)}
                        endIcon={
                          editLoading && <CircularProgress size="20px" />
                        }
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={() => handleCancelEdit(comment._id)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      dangerouslySetInnerHTML={{ __html: comment.text }}
                    />
                    <Box mt={0.5}>
                      {/* <Typography
                        variant='caption'
                        color='text.tertiary'
                        fontWeight='bold'
                        sx={{ mr:1, cursor:'pointer' }}
                        onClick={() => handleEditInit(comment._id, comment.text)}
                      >
                        Edit
                      </Typography> */}
                      <Typography
                        variant="caption"
                        color="text.tertiary"
                        fontWeight="bold"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleDeleteComment(comment._id)}
                      >
                        Delete
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

// Utility function
const formatTimeAgo = (differenceInMs) => {
  const mins = Math.floor(differenceInMs / (1000 * 60));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  const hrs = Math.floor(differenceInMs / (1000 * 60 * 60));
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export default Comments;
